import React, { forwardRef, type PropsWithChildren } from 'react';

import * as RadixPopover from '@radix-ui/react-popover';
import cn from 'classnames';

import { isPopupSafeElement } from '../../../helpers';

import styles from './Popover.module.css';
import { PopoverContentProps, PopoverRootProps } from './types';
import { usePopoverOpen } from './usePopoverOpen';

/**
 * Prevent the default event.
 * @param event - The event to prevent.
 */
const preventEventDefault = (event: Event) => {
  event.preventDefault();
};

/**
 * Root component for the Popover component.
 * This component is used to create a popover that can be opened and closed.
 */
const PopoverRoot = forwardRef<
  HTMLDivElement,
  PropsWithChildren<PopoverRootProps>
>((props, ref) => {
  const { onOpenChange, onPointerEnter, onPointerLeave, open } =
    usePopoverOpen(props);

  return (
    <RadixPopover.Root modal={false} open={open} onOpenChange={onOpenChange}>
      <div
        ref={ref}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
      >
        {props.children}
      </div>
    </RadixPopover.Root>
  );
});
PopoverRoot.displayName = 'PopoverRoot';

/**
 * Close button for the Popover component.
 * This component is used to close the popover.
 * Use this wrapper to properly close the popover
 */
const PopoverClose: React.FC<PropsWithChildren> = ({ children }) => {
  return <RadixPopover.Close asChild>{children}</RadixPopover.Close>;
};

/**
 * Anchor for the Popover component.
 * This component is used to anchor the popover to a specific element.
 * Use this wrapper to anchor the popover content to the specific element instead of the trigger
 */
const PopoverAnchor: React.FC<PropsWithChildren> = ({ children }) => {
  return <RadixPopover.Anchor asChild>{children}</RadixPopover.Anchor>;
};

/**
 * Trigger for the Popover component.
 * This component is used to trigger the popover.
 * Use this wrapper to trigger the popover opening
 */
const PopoverTrigger: React.FC<PropsWithChildren<{ asChild?: boolean }>> = ({
  children,
  asChild,
}) => {
  return (
    <RadixPopover.Trigger
      asChild={asChild}
      className={asChild ? '' : styles['popover-trigger-button']}
    >
      {children}
    </RadixPopover.Trigger>
  );
};

/**
 * Content for the Popover component.
 * This component is used to render the popover content.
 */
const PopoverContent = forwardRef<
  HTMLDivElement,
  PropsWithChildren<PopoverContentProps>
>(
  (
    {
      align,
      alignOffset = 0,
      side,
      sideOffset = 4,
      onInteractOutside,
      showArrowTip = false,
      preventOpenAutoFocus = false,
      children,
      className,
      dataTestId,
      width,
      height,
      deprecatedOverflowVisible,
    },
    ref
  ) => {
    return (
      <RadixPopover.Portal>
        <RadixPopover.Content
          ref={ref}
          align={align}
          alignOffset={alignOffset}
          className={cn(styles['popover-content'], className, {
            [styles['popover-content-overflow']]: deprecatedOverflowVisible,
          })}
          collisionPadding={10}
          data-testid={dataTestId}
          side={side}
          sideOffset={sideOffset}
          style={{ width, height }}
          onInteractOutside={(e) => {
            if (isPopupSafeElement(e.target)) {
              e.preventDefault();
            } else {
              onInteractOutside?.(e);
            }
          }}
          onOpenAutoFocus={
            preventOpenAutoFocus ? preventEventDefault : undefined
          }
        >
          {showArrowTip && (
            <RadixPopover.Arrow className={styles['popover-arrow']} />
          )}
          {children}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    );
  }
);
PopoverContent.displayName = 'PopoverContent';

export const Popover = {
  Root: PopoverRoot,
  Anchor: PopoverAnchor,
  Close: PopoverClose,
  Trigger: PopoverTrigger,
  Content: PopoverContent,
};
